import { __assign, __awaiter, __extends, __generator } from "tslib";
/* eslint-disable */
import EventEmitter from 'eventemitter3';
import pTimeout, { TimeoutError } from '../p-timeout';
import PriorityQueue from './priority-queue.js';
// eslint-disable-next-line @typescript-eslint/no-empty-function
var empty = function () { };
var timeoutError = new TimeoutError();
/**
The error thrown by `queue.add()` when a job is aborted before it is run. See `signal`.
*/
var AbortError = /** @class */ (function (_super) {
    __extends(AbortError, _super);
    function AbortError() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return AbortError;
}(Error));
export { AbortError };
/**
Promise queue with concurrency control.
*/
var PQueue = /** @class */ (function (_super) {
    __extends(PQueue, _super);
    function PQueue(options) {
        var _a, _b, _c, _d;
        var _this = _super.call(this) || this;
        _this._intervalCount = 0;
        _this._intervalEnd = 0;
        _this._pendingCount = 0;
        _this._resolveEmpty = empty;
        _this._resolveIdle = empty;
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        options = __assign({ carryoverConcurrencyCount: false, intervalCap: Number.POSITIVE_INFINITY, interval: 0, concurrency: Number.POSITIVE_INFINITY, autoStart: true, queueClass: PriorityQueue }, options);
        if (!(typeof options.intervalCap === 'number' && options.intervalCap >= 1)) {
            throw new TypeError("Expected `intervalCap` to be a number from 1 and up, got `" + ((_b = (_a = options.intervalCap) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : '') + "` (" + typeof options.intervalCap + ")");
        }
        if (options.interval === undefined || !(Number.isFinite(options.interval) && options.interval >= 0)) {
            throw new TypeError("Expected `interval` to be a finite number >= 0, got `" + ((_d = (_c = options.interval) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : '') + "` (" + typeof options.interval + ")");
        }
        _this._carryoverConcurrencyCount = options.carryoverConcurrencyCount;
        _this._isIntervalIgnored = options.intervalCap === Number.POSITIVE_INFINITY || options.interval === 0;
        _this._intervalCap = options.intervalCap;
        _this._interval = options.interval;
        _this._queue = new options.queueClass();
        _this._queueClass = options.queueClass;
        _this.concurrency = options.concurrency;
        _this._timeout = options.timeout;
        _this._throwOnTimeout = options.throwOnTimeout === true;
        _this._isPaused = options.autoStart === false;
        return _this;
    }
    Object.defineProperty(PQueue.prototype, "_doesIntervalAllowAnother", {
        get: function () {
            return this._isIntervalIgnored || this._intervalCount < this._intervalCap;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PQueue.prototype, "_doesConcurrentAllowAnother", {
        get: function () {
            return this._pendingCount < this._concurrency;
        },
        enumerable: false,
        configurable: true
    });
    PQueue.prototype._next = function () {
        this._pendingCount--;
        this._tryToStartAnother();
        this.emit('next');
    };
    PQueue.prototype._resolvePromises = function () {
        this._resolveEmpty();
        this._resolveEmpty = empty;
        if (this._pendingCount === 0) {
            this._resolveIdle();
            this._resolveIdle = empty;
            this.emit('idle');
        }
    };
    PQueue.prototype._onResumeInterval = function () {
        this._onInterval();
        this._initializeIntervalIfNeeded();
        this._timeoutId = undefined;
    };
    PQueue.prototype._isIntervalPaused = function () {
        var _this = this;
        var now = Date.now();
        if (this._intervalId === undefined) {
            var delay = this._intervalEnd - now;
            if (delay < 0) {
                // Act as the interval was done
                // We don't need to resume it here because it will be resumed on line 160
                this._intervalCount = this._carryoverConcurrencyCount ? this._pendingCount : 0;
            }
            else {
                // Act as the interval is pending
                if (this._timeoutId === undefined) {
                    this._timeoutId = setTimeout(function () {
                        _this._onResumeInterval();
                    }, delay);
                }
                return true;
            }
        }
        return false;
    };
    PQueue.prototype._tryToStartAnother = function () {
        if (this._queue.size === 0) {
            // We can clear the interval ("pause")
            // Because we can redo it later ("resume")
            if (this._intervalId) {
                clearInterval(this._intervalId);
            }
            this._intervalId = undefined;
            this._resolvePromises();
            return false;
        }
        if (!this._isPaused) {
            var canInitializeInterval = !this._isIntervalPaused();
            if (this._doesIntervalAllowAnother && this._doesConcurrentAllowAnother) {
                var job = this._queue.dequeue();
                if (!job) {
                    return false;
                }
                this.emit('active');
                job();
                if (canInitializeInterval) {
                    this._initializeIntervalIfNeeded();
                }
                return true;
            }
        }
        return false;
    };
    PQueue.prototype._initializeIntervalIfNeeded = function () {
        var _this = this;
        if (this._isIntervalIgnored || this._intervalId !== undefined) {
            return;
        }
        this._intervalId = setInterval(function () {
            _this._onInterval();
        }, this._interval);
        this._intervalEnd = Date.now() + this._interval;
    };
    PQueue.prototype._onInterval = function () {
        if (this._intervalCount === 0 && this._pendingCount === 0 && this._intervalId) {
            clearInterval(this._intervalId);
            this._intervalId = undefined;
        }
        this._intervalCount = this._carryoverConcurrencyCount ? this._pendingCount : 0;
        this._processQueue();
    };
    /**
      Executes all queued functions until it reaches the limit.
      */
    PQueue.prototype._processQueue = function () {
        // eslint-disable-next-line no-empty
        while (this._tryToStartAnother()) { }
    };
    Object.defineProperty(PQueue.prototype, "concurrency", {
        get: function () {
            return this._concurrency;
        },
        set: function (newConcurrency) {
            if (!(typeof newConcurrency === 'number' && newConcurrency >= 1)) {
                throw new TypeError("Expected `concurrency` to be a number from 1 and up, got `" + newConcurrency + "` (" + typeof newConcurrency + ")");
            }
            this._concurrency = newConcurrency;
            this._processQueue();
        },
        enumerable: false,
        configurable: true
    });
    /**
      Adds a sync or async task to the queue. Always returns a promise.
      */
    PQueue.prototype.add = function (fn, options) {
        if (options === void 0) { options = {}; }
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, new Promise(function (resolve, reject) {
                        var run = function () { return __awaiter(_this, void 0, void 0, function () {
                            var operation, result, error_1;
                            var _this = this;
                            var _a;
                            return __generator(this, function (_b) {
                                switch (_b.label) {
                                    case 0:
                                        this._pendingCount++;
                                        this._intervalCount++;
                                        _b.label = 1;
                                    case 1:
                                        _b.trys.push([1, 3, , 4]);
                                        if ((_a = options.signal) === null || _a === void 0 ? void 0 : _a.aborted) {
                                            // TODO: Use ABORT_ERR code when targeting Node.js 16 (https://nodejs.org/docs/latest-v16.x/api/errors.html_abort_err)
                                            reject(new AbortError('The task was aborted.'));
                                            return [2 /*return*/];
                                        }
                                        operation = this._timeout === undefined && options.timeout === undefined
                                            ? fn({ signal: options.signal })
                                            : pTimeout(Promise.resolve(fn({ signal: options.signal })), (options.timeout === undefined ? this._timeout : options.timeout), function () {
                                                if (options.throwOnTimeout === undefined ? _this._throwOnTimeout : options.throwOnTimeout) {
                                                    reject(timeoutError);
                                                }
                                                return undefined;
                                            });
                                        return [4 /*yield*/, operation];
                                    case 2:
                                        result = _b.sent();
                                        resolve(result);
                                        this.emit('completed', result);
                                        return [3 /*break*/, 4];
                                    case 3:
                                        error_1 = _b.sent();
                                        reject(error_1);
                                        this.emit('error', error_1);
                                        return [3 /*break*/, 4];
                                    case 4:
                                        this._next();
                                        return [2 /*return*/];
                                }
                            });
                        }); };
                        _this._queue.enqueue(run, options);
                        _this._tryToStartAnother();
                        _this.emit('add');
                    })];
            });
        });
    };
    /**
      Same as `.add()`, but accepts an array of sync or async functions.
  
      @returns A promise that resolves when all functions are resolved.
      */
    PQueue.prototype.addAll = function (functions, options) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                return [2 /*return*/, Promise.all(functions.map(function (function_) { return __awaiter(_this, void 0, void 0, function () { return __generator(this, function (_a) {
                        return [2 /*return*/, this.add(function_, options)];
                    }); }); }))];
            });
        });
    };
    /**
      Start (or resume) executing enqueued tasks within concurrency limit. No need to call this if queue is not paused (via `options.autoStart = false` or by `.pause()` method.)
      */
    PQueue.prototype.start = function () {
        if (!this._isPaused) {
            return this;
        }
        this._isPaused = false;
        this._processQueue();
        return this;
    };
    /**
      Put queue execution on hold.
      */
    PQueue.prototype.pause = function () {
        this._isPaused = true;
    };
    /**
      Clear the queue.
      */
    PQueue.prototype.clear = function () {
        this._queue = new this._queueClass();
    };
    /**
      Can be called multiple times. Useful if you for example add additional items at a later time.
  
      @returns A promise that settles when the queue becomes empty.
      */
    PQueue.prototype.onEmpty = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                // Instantly resolve if the queue is empty
                if (this._queue.size === 0) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve) {
                        var existingResolve = _this._resolveEmpty;
                        _this._resolveEmpty = function () {
                            existingResolve();
                            resolve();
                        };
                    })];
            });
        });
    };
    /**
      @returns A promise that settles when the queue size is less than the given limit: `queue.size < limit`.
  
      If you want to avoid having the queue grow beyond a certain size you can `await queue.onSizeLessThan()` before adding a new item.
  
      Note that this only limits the number of items waiting to start. There could still be up to `concurrency` jobs already running that this call does not include in its calculation.
      */
    PQueue.prototype.onSizeLessThan = function (limit) {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                // Instantly resolve if the queue is empty.
                if (this._queue.size < limit) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve) {
                        var listener = function () {
                            if (_this._queue.size < limit) {
                                _this.removeListener('next', listener);
                                resolve();
                            }
                        };
                        _this.on('next', listener);
                    })];
            });
        });
    };
    /**
      The difference with `.onEmpty` is that `.onIdle` guarantees that all work from the queue has finished. `.onEmpty` merely signals that the queue is empty, but it could mean that some promises haven't completed yet.
  
      @returns A promise that settles when the queue becomes empty, and all promises have completed; `queue.size === 0 && queue.pending === 0`.
      */
    PQueue.prototype.onIdle = function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                // Instantly resolve if none pending and if nothing else is queued
                if (this._pendingCount === 0 && this._queue.size === 0) {
                    return [2 /*return*/];
                }
                return [2 /*return*/, new Promise(function (resolve) {
                        var existingResolve = _this._resolveIdle;
                        _this._resolveIdle = function () {
                            existingResolve();
                            resolve();
                        };
                    })];
            });
        });
    };
    Object.defineProperty(PQueue.prototype, "size", {
        /**
          Size of the queue, the number of queued items waiting to run.
          */
        get: function () {
            return this._queue.size;
        },
        enumerable: false,
        configurable: true
    });
    /**
      Size of the queue, filtered by the given options.
  
      For example, this can be used to find the number of items remaining in the queue with a specific priority level.
      */
    PQueue.prototype.sizeBy = function (options) {
        // eslint-disable-next-line unicorn/no-array-callback-reference
        return this._queue.filter(options).length;
    };
    Object.defineProperty(PQueue.prototype, "pending", {
        /**
          Number of running items (no longer in the queue).
          */
        get: function () {
            return this._pendingCount;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PQueue.prototype, "isPaused", {
        /**
          Whether the queue is currently paused.
          */
        get: function () {
            return this._isPaused;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(PQueue.prototype, "timeout", {
        get: function () {
            return this._timeout;
        },
        /**
          Set the timeout for future operations.
          */
        set: function (milliseconds) {
            this._timeout = milliseconds;
        },
        enumerable: false,
        configurable: true
    });
    return PQueue;
}(EventEmitter));
export default PQueue;
//# sourceMappingURL=index.js.map